import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { supabase } from './lib/supabase';

export async function registerPushNotifications(lidId) {
  if (!Capacitor.isNativePlatform()) {
    // Web/TWA: alleen stil opnieuw opslaan als het lid al toestemming gaf.
    // Toestemming vragen gebeurt via de MeldingenKaart (na een tik).
    registerWebPush(lidId).catch(e => console.error('Web push-registratie mislukt:', e));
    return;
  }

  const permStatus = await PushNotifications.checkPermissions();
  let finalStatus = permStatus;

  if (permStatus.receive === 'prompt') {
    finalStatus = await PushNotifications.requestPermissions();
  }

  if (finalStatus.receive !== 'granted') return;

 PushNotifications.addListener('registration', async ({ value: token }) => {
    const platform = Capacitor.getPlatform();
    
    await supabase
      .from('device_tokens')
      .delete()
      .eq('lid_id', lidId)
      .eq('platform', platform);
    
    const { error } = await supabase.from('device_tokens').insert(
      { lid_id: lidId, token, platform }
    );
    if (error) console.error('Push token fout:', JSON.stringify(error));
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('Push registratie fout:', err);
  });

  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push ontvangen:', notification);
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('Push actie:', action);
  });

  await PushNotifications.register();
}

// ── Web push (Android/TWA en de website) ─────────────────
// Sinds juli 2026 is de Android-app een TWA (de website in een Play Store-jasje).
// Daar werkt de native Capacitor-push niet; meldingen lopen via web push.
// Het abonnement (een JSON-tekst) komt in device_tokens met platform 'web'.
// De publieke VAPID-sleutel staat in REACT_APP_VAPID_PUBLIC_KEY (Vercel);
// de geheime sleutel staat alleen als Supabase-secret bij hyper-responder.
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

// 'unsupported' | 'default' | 'granted' | 'denied'
export function webPushStatus() {
  if (Capacitor.isNativePlatform()) return 'unsupported'
  if (!VAPID_PUBLIC_KEY) return 'unsupported'
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported'
  }
  return Notification.permission
}

// vraagToestemming: true = toestemmingsvenster tonen (alleen na een tik van het lid).
// Zonder vraagToestemming wordt alleen een bestaand/toegestaan abonnement (opnieuw) opgeslagen.
export async function registerWebPush(lidId, { vraagToestemming = false } = {}) {
  if (webPushStatus() === 'unsupported') return false

  let permissie = Notification.permission
  if (permissie === 'default' && vraagToestemming) {
    permissie = await Notification.requestPermission()
  }
  if (permissie !== 'granted') return false

  const registratie = await navigator.serviceWorker.ready
  let abonnement = await registratie.pushManager.getSubscription()
  if (!abonnement) {
    abonnement = await registratie.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    })
  }

  const token = JSON.stringify(abonnement.toJSON())
  const { error } = await supabase
    .from('device_tokens')
    .insert({ lid_id: lidId, token, platform: 'web' })
  // 23505 = dit abonnement staat er al; dat is prima.
  if (error && error.code !== '23505') {
    console.error('Web push-abonnement opslaan mislukt:', JSON.stringify(error))
    return false
  }
  return true
}

// Web/TWA: meldingen die nog in het meldingenpaneel staan opruimen zodra het lid
// de app opent. Op Android verdwijnt daarmee ook het rode bolletje op het icoon.
export async function wisWebMeldingen() {
  if (Capacitor.isNativePlatform() || !('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const meldingen = reg ? await reg.getNotifications() : [];
    meldingen.forEach(n => n.close());
    if ('clearAppBadge' in navigator) await navigator.clearAppBadge();
  } catch (e) {
    console.error('Meldingen opruimen mislukt:', e);
  }
}
